<template>
    <div>  
      <div class="container mx-auto my-8 p- bg-white rounded-lg">
        <div v-if="isLoading" class="flex justify-center items-center py-20">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"></div>
        </div>
        <div v-else-if="!isLoading && paginatedContributors.length > 0" class="space-y-6">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y">
              <thead class="text-[12px] text-gray-500 bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left font-medium tracking-wider">Name</th>
                  <th scope="col" class="px-6 py-3 text-left font-medium tracking-wider">System Roles</th>
                  <th scope="col" class="px-6 py-3 text-left font-medium tracking-wider">Skills</th>
                  <th scope="col" class="px-6 py-3 text-left font-medium tracking-wider">User Roles</th>
                  <th scope="col" class="px-6 py-3 text-left font-medium tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200 text-[13px]">
                <tr
                  v-for="contributor in paginatedContributors"
                  :key="contributor.id"
                  class="hover:bg-gray-50 transition text-bodyText"
                >
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                      <div
                        v-if="contributor.profile_picture"
                        class="w-[40px] h-[40px] rounded-full overflow-hidden"
                      >
                        <img
                          :src="contributor.profile_picture"
                          :alt="contributor.name"
                          class="w-full h-full object-cover"
                        />
                      </div>
                      <div
                        v-else
                        class="w-[40px] h-[40px] flex items-center justify-center rounded-full text-white font-semibold text-sm"
                        :style="{ backgroundColor: getRandomColor(contributor.id) }"
                      >
                        {{ contributor.initials }}
                      </div>
                      <div>
                        <p class="font-medium text-gray-900">
                          {{ contributor.name }}
                        </p>
                        <p class="text-gray-500 text-xs">{{ contributor.email }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 text-gray-700">
                    {{ capitalizeFirstLetter(contributor.systemRole) }}
                  </td>
                  <td class="px-6 py-4">
                    <span v-if="contributor.skills && contributor.skills !== '-'" class="inline-flex px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                      {{ contributor.skills }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-gray-700">
                    {{ capitalizeFirstLetter(contributor.userRole) }}
                  </td>
                  <td class="px-6 py-4">
                    <div v-if="isAdmin || isManager" class="relative">
                      <button
                        @click.stop="contributor.systemRole?.toLowerCase() !== 'owner' && toggleDropdown(contributor.id)"
                        :disabled="contributor.systemRole?.toLowerCase() === 'owner'"
                        :class="[
                          'p-1 rounded-lg',
                          contributor.systemRole?.toLowerCase() === 'owner' 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        ]"
                      >
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                        </svg>
                      </button>
                      
                      <!-- Dropdown Menu -->
                      <div
                        v-if="dropdownOpen === contributor.id && contributor.systemRole?.toLowerCase() !== 'owner'"
                        class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                      >
                        <div class="py-1">
                          <button
                            @click="viewProfile(contributor.id)"
                            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            View Profile
                          </button>
                          <button
                            v-if="!contributor.manager_id"
                            @click="openUpdatePositionModal(contributor)"
                            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Update User Role
                          </button>
                          <button
                            @click="openRemoveModal(contributor.member_id, contributor.name)"
                            class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      v-else
                      @click="viewProfile(contributor.id)"
                      class="text-sm text-gray-600 hover:text-gray-900"
                    >
                      View profile
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <div class="flex items-center justify-between px-6 py-4 border-t">
            <button
              @click="goToPreviousPage"
              :disabled="currentPage === 1"
              class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Previous
            </button>
            
            <div class="flex items-center gap-2">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-3 py-1 text-sm rounded-lg',
                  currentPage === page 
                    ? 'bg-[#F9FAFB] text-[#344054]' 
                    : 'text-gray-700 hover:bg-gray-100'
                ]"
              >
                {{ page === '...' ? '...' : page }}
              </button>
            </div>
            
            <button
              @click="goToNextPage"
              :disabled="currentPage === totalPages"
              class="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
        <div v-else-if="!isLoading && contributors.length === 0" class="text-center text-bodyText">
          <p class="mx-auto bg-primaryLight px-2 py-24 border border-grayLight rounded-[16px]">
            No Contributors available.
          </p>
        </div>
      </div>

      <!-- Remove Contributor Modal -->
      <RemoveContributorModal
        :isOpen="removeModalOpen"
        :selectedContributor="selectedContributor"
        @close="closeRemoveModal"
        @confirm-remove="confirmRemoveContributor"
      />

      <Teleport to="body">
        <div
          v-if="updatePositionModalOpen"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          @click.self="closeUpdatePositionModal"
        >
          <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Update System Role</h3>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                System Role for {{ selectedContributorForUpdate?.name }}
              </label>
              <input
                v-model="newPosition"
                type="text"
                placeholder="Enter new position"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                @keyup.enter="confirmUpdatePosition"
              />
            </div>

            <div class="flex justify-end gap-3">
              <button
                @click="closeUpdatePositionModal"
                class="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                @click="confirmUpdatePosition"
                :disabled="!newPosition.trim()"
                class="px-4 py-2 text-sm text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  
  </template>
  
  
  
  <script>
  import apiClient from '@/axios';
  import RemoveContributorModal from '@/components/modals/RemoveContributorModal.vue';
  
  export default {
    name: 'ContributorsTable',
    components: {
      RemoveContributorModal,
    },
    props: {
      projectId: {
        type: String,
        required: true
      },
      selectedRole: {
        type: String,
        default: ''
      },
      selectedSkill: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        contributors: [], 
        managedProjects: [],
        isLoading: true,
        selectedTalent: {
          first_name: '',
          last_name: '',
          email: ''
        },
        availableSkills: ['Project management', 'Design', 'Development', 'Marketing'],
        dropdownOpen: null,
        removeModalOpen: false,
        selectedContributor: {
          id: null,
          name: ''
        },
        updatePositionModalOpen: false,
        selectedContributorForUpdate: null,
        newPosition: '',
        showRoleDropdown: false,
        showSkillsDropdown: false,
        roles: ['Mentor', 'Project Owner', 'Project Manager', 'Software Engineer', 'Product Designer', 'UX Writer'],
        currentPage: 1,
        itemsPerPage: 8,
        colors: ['#A3DB7D', '#7DB4DB', '#DB7DA3', '#DBA37D', '#7DDBA3', '#A37DDB']
      };
    },
    computed: {
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isManager() {
        return this.$store.getters.isManager;
      },
      totalPages() {
        return Math.ceil(this.contributors.length / this.itemsPerPage);
      },
      paginatedContributors() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.contributors.slice(start, end);
      },
      visiblePages() {
        const pages = [];
        const maxVisible = 7;
        
        if (this.totalPages <= maxVisible) {
          for (let i = 1; i <= this.totalPages; i++) {
            pages.push(i);
          }
        } else {
          if (this.currentPage <= 3) {
            for (let i = 1; i <= 5; i++) {
              pages.push(i);
            }
            pages.push('...');
            pages.push(this.totalPages);
          } else if (this.currentPage >= this.totalPages - 2) {
            pages.push(1);
            pages.push('...');
            for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
              pages.push(i);
            }
          } else {
            pages.push(1);
            pages.push('...');
            for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
              pages.push(i);
            }
            pages.push('...');
            pages.push(this.totalPages);
          }
        }
        
        return pages;
      }
    },
    watch: {
      projectId: {
        immediate: true,
        handler(newProjectId) {
          if (newProjectId) {
            this.fetchContributors(newProjectId, this.selectedRole, this.selectedSkill);
          }
        }
      },
      selectedRole(newRole) {
        this.currentPage = 1;
        if (this.projectId) {
          this.fetchContributors(this.projectId, newRole, this.selectedSkill);
        }
      },
      selectedSkill(newSkill) {
        this.currentPage = 1;
        if (this.projectId) {
          this.fetchContributors(this.projectId, this.selectedRole, newSkill);
        }
      }
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
      handleClickOutside(event) {
        if (!event.target.closest('.relative')) {
          this.dropdownOpen = null;
        }
      },
      getInitials(name) {
        if (!name) return '';
        const parts = name.split(' ');
        const firstInitial = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
        const lastInitial = parts.length > 1 ? parts[1].charAt(0).toUpperCase() : '';
        return `${firstInitial}${lastInitial}`;
      },
      async fetchContributors(projectId, role = null, skill = null) {
        try {
          this.isLoading = true;
          const params = {};
          if (role) params.role = role;
          if (skill) params.skill = skill;
          
          const response = await apiClient.get(`/web/projects/${projectId}/contributors`, { params });
          const contributors = response.data.data || [];

          this.contributors = contributors.map((contributor, index) => ({
            id: contributor.user_id,
            member_id: contributor.member_id,
            manager_id: contributor.manager_id,
            talent_id: contributor.talent_id,
            initials: this.getInitials(contributor.name),
            project_map_id: index,
            name: contributor.name,
            email: contributor.email,
            profile_picture: contributor.profile_picture,
            systemRole: contributor.system_role,
            skills: Array.isArray(contributor.skills) 
              ? (contributor.skills.length > 0 ? contributor.skills.join(', ') : '-')
              : contributor.skills || '-',
            userRole: contributor.user_role,
          }));
        } catch (error) {
          console.error('Error fetching contributors:', error);
        } finally {
          this.isLoading = false;
        }
      },

      getRandomColor(id) {
        return this.colors[id % this.colors.length];
      },

      capitalizeFirstLetter(text) {
        if (!text || text === '-') return text;
        return text.charAt(0).toUpperCase() + text.slice(1);
      },

      toggleRoleDropdown() {
        this.showRoleDropdown = !this.showRoleDropdown;
        if (this.showRoleDropdown) {
          this.showSkillsDropdown = false;
        }
      },

      toggleSkillsDropdown() {
        this.showSkillsDropdown = !this.showSkillsDropdown;
        if (this.showSkillsDropdown) {
          this.showRoleDropdown = false;
        }
      },

      addTalent() {
        // Implement add talent logic
        this.$emit('add-talent');
      },

      viewProfile(userId) {
        this.$router.push(`/explore/profile/${userId}`);
      },

      openUpdatePositionModal(contributor) {
        if (!contributor.manager_id && contributor.systemRole !== 'manager') {
          this.dropdownOpen = null;
          this.selectedContributorForUpdate = contributor;
          this.newPosition = contributor.userRole || '';
          this.updatePositionModalOpen = true;
        }
      },

      closeUpdatePositionModal() {
        this.updatePositionModalOpen = false;
        this.selectedContributorForUpdate = null;
        this.newPosition = '';
      },

      async confirmUpdatePosition() {
        if (!this.newPosition.trim()) return;

        try {

          await apiClient.patch(
            `/web/projects/${this.projectId}/project_members/${this.selectedContributorForUpdate.member_id}`,
            { position: this.newPosition }
          );

          const contributor = this.contributors.find(
            c => c.member_id === this.selectedContributorForUpdate.member_id
          );
          if (contributor) {
            contributor.userRole = this.newPosition;
          }

          this.$toast.success(`Position updated successfully for ${this.selectedContributorForUpdate.name}`);
          this.closeUpdatePositionModal();
        } catch (error) {
          console.error('Error updating position:', error);
          this.$toast.error('Failed to update position. Please try again.');
          this.closeUpdatePositionModal();
        }
      },

      goToPreviousPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
        }
      },

      goToNextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
        }
      },

      goToPage(page) {
        if (page !== '...' && page >= 1 && page <= this.totalPages) {
          this.currentPage = page;
        }
      },

      goToReports(userId) {
        if (!userId) {
          console.error("Missing required parameters for navigation.");
          return;
        }

        // Navigate to the report page for the specific project and user
        this.$router.push({ name: 'ProgressReports', params: { projectId: this.projectId, userId } });
      },
      toggleDropdown(projectMapId) {
        this.dropdownOpen = this.dropdownOpen === projectMapId ? null : projectMapId;
      },
      getDropdownPosition(projectMapId) {
        const contributorIndex = this.contributors.findIndex(c => c.project_map_id === projectMapId);
        const totalContributors = this.contributors.length;
        
        if (contributorIndex === totalContributors - 1 || totalContributors === 1) {
          return 'bottom-full mb-2'; 
        } else {
          return 'top-full mt-2'; 
        }
      },
      openRemoveModal(memberId, contributorName) {
        if(memberId === null) return;

        this.selectedContributor = {
          id: memberId,
          name: contributorName
        };
        this.removeModalOpen = true;
        this.dropdownOpen = null; 
      },
      closeRemoveModal() {
        this.removeModalOpen = false;
        this.selectedContributor = {
          id: null,
          name: ''
        };
      },
      async confirmRemoveContributor(memberId) {
        try {
          const contributor = this.contributors.find(c => c.member_id === memberId);
          if (!contributor) {
            this.$toast.error('Contributor not found');
            return;
          }

          const isManagerOrSystemRole = contributor.manager_id || contributor.systemRole !== "-";
          
          if (isManagerOrSystemRole) {
            await apiClient.delete(`/web/projects/${this.projectId}/project_managers/${contributor.manager_id}`);
          } else {
            await apiClient.delete(`/web/projects/${this.projectId}/project_members/${contributor.member_id}`);
          }
          
          this.contributors = this.contributors.filter(c => c.member_id !== memberId);
          
          this.closeRemoveModal();
          
          this.$toast.success(`${contributor.name} has been removed from the project successfully.`);
          
        } catch (error) {
          // Show error message
          this.$toast.error(error.response?.data?.message || 'Failed to remove contributor. Please try again.');
          
          // Close the modal even on error
          this.closeRemoveModal();
        }
      },
    }
  };
  </script>
  
<style scoped>
  .container {
    max-width: 1120px;
    margin: auto;
  }
  
  h1, h2 {
    color: #333;
  }
  
  table {
    border-collapse: collapse;
  }
  
  th, td {
    padding: 1rem;
  }
  
  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  button {
    font-weight: 500;
  }
</style>
  